import React, { ReactNode } from "react";
import { api } from "api/api";
import MacroButton from "./MacroButton.component";
import { DialogContentText } from "@mui/material";

export default function RenameButton({
  renameType,
  oldName,
  newName,
}: {
  renameType: "subproperty" | "PVA";
  oldName: string;
  newName: string;
}) {
  const oldNameWithSeparator =
    renameType === "subproperty" ? "." + oldName : "::" + oldName;
  const newNameWithSeparator =
    renameType === "subproperty" ? "." + newName : "::" + newName;

  const renameFunction =
    renameType === "subproperty"
      ? api.renameSubproperty.bind(api)
      : api.renamePVA.bind(api);

  const performApiCall = (productLineId: string) =>
    renameFunction(productLineId, oldName, newName);

  const apiCallDependencies = [
    newName,
    newNameWithSeparator,
    oldName,
    oldNameWithSeparator,
    renameFunction,
    renameType,
  ];

  const dialogTitle =
    renameType === "subproperty"
      ? `Replace ${oldNameWithSeparator} Subproperties with ${newNameWithSeparator}`
      : `Replace ${oldNameWithSeparator} with ${newNameWithSeparator}`;

  function InfoMessage(): ReactNode {
    return renameType === "subproperty" ? (
      <DialogContentText>
        You are about to rename all uses of {oldNameWithSeparator} in this
        product line to {newNameWithSeparator}. This includes subproperty names,
        PVA names, and rules tables. Are you sure you want to continue?
      </DialogContentText>
    ) : (
      <DialogContentText>
        You are about to rename all uses of {oldNameWithSeparator} in this
        product line to {newNameWithSeparator}. This includes the names of
        specific PVA properties, PVAs at the product line level, and rules
        tables. Are you sure you want to continue?
      </DialogContentText>
    );
  }

  return (
    <MacroButton
      performApiCall={performApiCall}
      apiCallDependencies={apiCallDependencies}
      buttonLabel={`Replace ${oldNameWithSeparator} with ${newNameWithSeparator}`}
      dialogTitle={dialogTitle}
      tooltipText={`Renames every occurence of ${oldNameWithSeparator} in product line to ${newNameWithSeparator}`}
      processingMessage={`Renaming ${oldNameWithSeparator} to ${newNameWithSeparator}. This may take a while.`}
      completionMessage="Rename completed. See detailed results below."
      timeOutMessage="Rename timed out. See details below."
      informationContent={InfoMessage()}
      timeOutFailureDescription="Request timed out. This could be due to an especially large number of renames."
      pvasUpdated={renameType === "PVA"}
      recommendValidation={true}
    />
  );
}
